import {
  Box,
  Button,
  CircularProgress,
  Divider,
  IconButton,
  Link,
} from '@material-ui/core';
import {
  createShopifyOrder,
  removeProductFromShoppingCart,
  setShoppingTabView,
} from '@src/reducers/shopify';
import { ShopifyTabsView } from '@src/reducers/shopify/types';
import {
  getShopifyShoppingCart,
  getShopifyShoppingCartIsLoading,
} from '@src/selectors/shopify';
import { ShoppingCart } from '@src/types/shopify';
import { useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';
import ShoppingCartItemWrapper from './ShoppingCartItem';
import { CopyIcon, EmptyShoppingCartIcon, GoBackIcon } from './SvgIcons';
import { TotalAmount } from './Utils';

export interface IShoppingCart {
  shoppingCart: ShoppingCart;
  isLoading?: boolean;
  handleClickOnBack: () => void;
  handleCleanCart: () => void;
  handleCreateOrder: () => void;
}

const ShoppingCartWrapper: React.FC = () => {
  const dispatch = useDispatch();
  const intl = useIntl();
  const { formatMessage } = intl;
  const shoppingCart = useSelector(getShopifyShoppingCart);
  const isLoading = useSelector(getShopifyShoppingCartIsLoading);

  const handleClickOnBack = () => {
    dispatch(setShoppingTabView(ShopifyTabsView.PRODUCT_LIST));
  };

  const handleCleanCart = () => {
    const cartProductIds = shoppingCart.products.map(item => item.id);
    dispatch(
      removeProductFromShoppingCart({
        cartLinesId: [...cartProductIds],
      })
    );
  };

  const handleCreateOrder = () => {
    dispatch(
      createShopifyOrder({
        textMessage: formatMessage({
          id: 'Shopify.shoppingCart.ordersHistory.checkoutUrl.textMessage',
        }),
      })
    );
  };

  return (
    <ShoppingCartView
      shoppingCart={shoppingCart}
      isLoading={isLoading}
      handleClickOnBack={handleClickOnBack}
      handleCleanCart={handleCleanCart}
      handleCreateOrder={handleCreateOrder}
    />
  );
};

export const ShoppingCartView: React.FC<IShoppingCart> = ({
  shoppingCart,
  isLoading,
  handleClickOnBack,
  handleCleanCart,
  handleCreateOrder,
}) => {
  const intl = useIntl();
  const { formatMessage } = intl;
  const isEmpty = !shoppingCart || shoppingCart?.products.length === 0;

  const actionButtons = isEmpty ? (
    <Button
      color="primary"
      variant="contained"
      fullWidth
      size="small"
      onClick={handleClickOnBack}
      data-testid="shopping-cart-empty-back-button"
    >
      {formatMessage({ id: 'Shopify.shoppingCart.addProducts.button.label' })}
    </Button>
  ) : (
    <Box display="flex" justifyContent="center" style={{ gap: '20px' }}>
      <Button
        color="primary"
        variant="outlined"
        size="small"
        style={{ width: '130px' }}
        onClick={handleCleanCart}
        data-testid="shopping-cart-clean-button"
      >
        {formatMessage({ id: 'Shopify.shoppingCart.emptyCart.button.label' })}
      </Button>
      <Button
        color="primary"
        variant="contained"
        size="small"
        style={{ width: '130px' }}
        onClick={handleCreateOrder}
        data-testid="shopping-cart-create-order-button"
      >
        {formatMessage({ id: 'Shopify.shoppingCart.createOrder.button.label' })}
      </Button>
    </Box>
  );

  const productsView = () => {
    const { products, cost, checkoutUrl } = shoppingCart;
    const { amount: subTotal, currencyCode } = cost.subtotalAmount;
    return (
      <>
        <Box
          flexGrow={1}
          margin="8px 16px"
          overflow="auto"
          padding="2px 10px 2px 2px"
          height="calc(100vh - 408px)"
        >
          {products.map((item, ind) => (
            <ShoppingCartItemWrapper
              key={item.id}
              item={{
                ...item,
                isFirst: ind === 0,
                isLast: ind + 1 === products.length,
              }}
            />
          ))}
        </Box>
        <Box margin="0px 16px">
          <TotalAmount subTotal={Number(subTotal)} currency={currencyCode} />
          <Box
            pt={1}
            pb={1}
            display="flex"
            justifyContent="space-between"
            alignItems="center"
          >
            <Link
              href={checkoutUrl}
              target="_blank"
              rel="noreferrer"
              style={{
                textDecoration: 'underline',
                fontSize: 12,
                fontWeight: '500',
                lineHeight: '130%',
              }}
            >
              {formatMessage({ id: 'Shopify.shoppingCart.link.label' })}
            </Link>

            <IconButton
              onClick={() => navigator.clipboard.writeText(checkoutUrl)}
              style={{ cursor: 'pointer' }}
            >
              {CopyIcon}
            </IconButton>
          </Box>
          <Divider style={{ height: '2px' }} />
        </Box>
      </>
    );
  };

  return (
    <Box
      display="flex"
      flexDirection="column"
      justifyContent="space-between"
      flexGrow={1}
    >
      <Box display="flex" alignItems="center" margin="16px">
        <Box onClick={handleClickOnBack} style={{ cursor: 'pointer' }}>
          {GoBackIcon}
        </Box>
        <Box textAlign="center" fontSize={16} flex={2}>
          {formatMessage({ id: 'Shopify.shoppingCart.title' })}
        </Box>
      </Box>
      {isEmpty && !isLoading && (
        <Box
          display="flex"
          flexDirection="column"
          justifyContent="center"
          alignItems="center"
          flexGrow={1}
          fontSize={16}
          fontWeight={500}
          style={{ gap: '17px' }}
          textAlign="center"
        >
          {EmptyShoppingCartIcon}
          {formatMessage({ id: 'Shopify.shoppingCart.empty.text' })}
        </Box>
      )}
      {isLoading && (
        <Box
          display="flex"
          alignItems="center"
          justifyContent="center"
          paddingTop={10}
        >
          <CircularProgress />
        </Box>
      )}
      {!isEmpty && !isLoading && productsView()}
      <Box margin={2}>{actionButtons}</Box>
    </Box>
  );
};

export default ShoppingCartWrapper;
